import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, DrawerBody, DrawerFooter, HStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../components/DropdownMenu';
import { findSelected } from '../../../../components/DropdownMenu/util';
import FormInput from '../../../../components/form/FormInput';
import InputLabel from '../../../../components/form/InputLabel';
import RequirementTemplateDropdownMenu from '../../../../components/RequirementTemplateDropdownMenu';
import LoadingSpinner from '../../../../components/Spinner';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { CypressIds } from '../../../../constants/cypressIds';
import { createNewJob, selectCurrentlyCreatingJob, selectJobSelectOptions } from '../../../../redux/modules/jobs';
import { jobSearch } from '../../../../redux/modules/jobSearch';
import { hideTray, updateTrayProps } from '../../../../redux/modules/tray';
import { activeDepartments } from '../../../../utils/departmentUtil';

import { dateFormStack, formStack, validateDates } from './util';

const CreateJobForm = ({ companyId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentlyCreatingJob = useSelector(selectCurrentlyCreatingJob);

  const useFormMethods = useForm();
  const { handleSubmit, control, setValue } = useFormMethods;
  const { isDirty } = useFormState({ control });

  useEffect(() => {
    dispatch(updateTrayProps({ formIsDirty: isDirty }));
  }, [isDirty]);

  const [isLoading, setIsLoading] = useState(true);

  const { ownerOptions, departmentOptions: allDepartmentOptions } = useSelector(selectJobSelectOptions);
  const departmentOptions = useMemo(
    () => allDepartmentOptions && activeDepartments(allDepartmentOptions),
    [allDepartmentOptions]
  );
  const [selectedOwner, setOwner] = useState({ label: '', id: null });
  const [selectedDepartment, setDepartment] = useState();

  useEffect(() => {
    if (!ownerOptions || !departmentOptions) {
      setIsLoading(true);
      return;
    }

    setDepartment(departmentOptions[0]);
    setIsLoading(false);
  }, [departmentOptions, ownerOptions]);

  const handleDepartmentClick = selectedItem => {
    const selected = findSelected(departmentOptions, Number(selectedItem.value));
    setDepartment(selected);
  };

  const handleOwnerClick = selectedItem => {
    const selected = findSelected(ownerOptions, Number(selectedItem.value));
    setOwner(selected);
  };

  const onSubmit = async data => {
    const { jobName, jobNumber, jobDescription } = data;

    const departmentId = selectedDepartment?.id
      ? selectedDepartment.id
      : departmentOptions[0]?.id;

    const newJobData = {
      job_name: jobName,
      ...(jobNumber && { job_number: jobNumber }),
      job_description: jobDescription,
      job_start: data.job_start,
      job_end: data.job_end,
      department_id: departmentId,
      owner_id: selectedOwner.id,
      default_requirement_template_id: data.default_requirement_template_id
    };

    const response = await dispatch(createNewJob(newJobData));

    if (response.success) {
      dispatch(jobSearch.getPage());
      history.push(`projects/${response.job.jobId}`);
      dispatch(hideTray());
    }
  };

  return (
    <>
      <DrawerBody>
        {isLoading
          ? <LoadingSpinner />
          : (
            <form dusk="newJobForm" id="newJobForm" onSubmit={handleSubmit(onSubmit)}>
              {formStack.map(input => (
                <FormInput
                  disableEnterKey
                  cypressId={input.cypressId}
                  dataAnalyticsId={input.dataAnalyticsId}
                  inputName={input.inputName}
                  key={input.inputName}
                  labelName={input.labelName}
                  placeholder={input.placeholder}
                  required={input.required}
                  toolTip={input?.tooltip}
                  type={input?.type}
                  useFormMethods={useFormMethods}
                  validationRules={input?.validation}
                />
              ))}
              <Box height="95px">
                <InputLabel
                  required
                  labelName="Department"
                  toolTip={`A department must be selected to create a project. The default department ${departmentOptions[0]?.label}`
                                        + ' will be applied unless a different department is selected.'}
                />
                <DropdownMenu
                  dataAnalyticsKey={analyticsIds.projectsListPage.createProjectForm.departmentDropdown}
                  options={departmentOptions}
                  placeholder="Project Department"
                  selected={selectedDepartment?.label}
                  width="400px"
                  onSelect={handleDepartmentClick}
                />
              </Box>
              <HStack height="95px" spacing={2}>
                {dateFormStack.map(input => (
                  <FormInput
                    disableEnterKey
                    inputName={input.inputName}
                    key={input.inputName}
                    labelName={input.labelName}
                    maxDate={input.inputName === 'job_start' && useFormMethods.watch('job_end')}
                    minDate={input.inputName === 'job_end' && useFormMethods.watch('job_start')}
                    placeholder={input.placeholder}
                    required={input.required}
                    type="date"
                    useFormMethods={useFormMethods}
                    validationRules={{ validate: value => validateDates(value, input.inputName, useFormMethods) }}
                  />
                ))}
              </HStack>
              <Box height="95px">
                <InputLabel
                  labelName="Project Owner"
                  toolTip="Project owner is the main contact for this project"
                />
                <DropdownMenu
                  dataAnalyticsKey={analyticsIds.projectsListPage.createProjectForm.projectOwnerDropdown}
                  options={ownerOptions}
                  placeholder="Project Owner"
                  selected={selectedOwner.label}
                  width="400px"
                  onSelect={selected => handleOwnerClick(selected)}
                />
              </Box>
              <Box height="95px">
                <InputLabel
                  labelName="Default Insurance Requirements"
                  toolTip="Set the default insurance requirements for this project."
                />
                <RequirementTemplateDropdownMenu
                  companyId={companyId}
                  variant="primary"
                  onChange={value => setValue('default_requirement_template_id', value?.id ?? null)}
                />
              </Box>

            </form>
          )}
      </DrawerBody>
      <DrawerFooter>
        <Button
          data-analytics-id={analyticsIds.projectsListPage.createProjectForm.saveProjectButton}
          data-cy={CypressIds.createProjectTraySubmitButton}
          dusk={analyticsIds.projectsListPage.createProjectForm.saveProjectButton}
          form="newJobForm"
          isDisabled={currentlyCreatingJob}
          mt={3}
          type="submit"
          w="100%"
        >
          Save Project
        </Button>
      </DrawerFooter>
    </>
  );
};

CreateJobForm.propTypes = { companyId: PropTypes.number.isRequired };

export default CreateJobForm;
